import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  useDevOverride,
  type CurrentPeriod,
} from '../../context/DevOverrideContext';
import {
  listScheduleTypes,
  listPeriods,
  getSchool,
} from '../../api/schoolConfigApi';
import { logError } from '../../utils/errorHandling';
import CollapsibleSection from '../common/CollapsibleSection';
import LoadingScreen from '../common/LoadingScreen';
import SchoolSetupWizard, { DEFAULT_VARIANT_NAME } from './SchoolSetupWizard';
import SetupChecklist from './SetupChecklist';
import PassHistoryView from './PassHistoryView';
import ActivePassesPanel from './panels/ActivePassesPanel';
import UserManagementPanel from './panels/UserManagementPanel';
import DestinationsPanel from './panels/DestinationsPanel';
import SchoolPeriodsPanel from './panels/SchoolPeriodsPanel';
import ScheduleConfigPanel, {
  groupScheduleTypes,
  type SchoolConfig,
} from './panels/ScheduleConfigPanel';
import PassPolicyPanel from './panels/PassPolicyPanel';
import StudentPassLimitsPanel from './panels/StudentPassLimitsPanel';
import ClassSectionsPanel from './panels/ClassSectionsPanel';
import styles from './AdminPanel.module.css';

// Thin composition shell for the admin experience — rewritten for the
// Firebase -> hallpass-backend migration (Admin step 10, the integration
// step). All data fetching now lives in the panel components below
// (src/components/admin/panels/*.js, built in steps 3-9); this file keeps
// only auth/role gating, schoolId resolution, the SchoolSetupWizard gate,
// and panel composition behind the same on-page CollapsibleSection layout
// the legacy AdminPanel.js used (there is no route-level tab system here —
// every section is a collapsible, localStorage-persisted panel on one
// scrolling page, and each panel below wraps itself in its own
// CollapsibleSection, so this shell just renders them in sequence).
//
// Confirmed removals from the step-1 decomposition manifest (all of the
// legacy AdminPanel.js's onSnapshot listeners, httpsCallable-backed
// handlers, and JSX for these are gone, not carried into any panel):
// Announcement admin UI + the read-side AnnouncementBanner, Student
// Schedule Management CSV upload (uploadStudentSchedules), Student Data
// Management pass-history CSV upload (uploadPassHistory/
// confirmPassHistoryUpload), bulk/manual approved-emails management
// (addApprovedEmail/removeApprovedEmail/addBulkEmails) and its Google Auth
// + Blocked Users sub-tabs, processQueueIntegrityV2 (Queue Integrity
// Tools), runOrganizationMigration, normalizeUserEmails, and the bulk
// createUserAccounts handler + approved-emails pagination/filter cluster
// (getFilteredEmails/getPaginatedEmails/getEmailPagination/
// handleEmailPageChange/handleEmailFilterChange) — both of the latter two
// were confirmed-unreachable-from-JSX leftovers from before UserManagementPanel
// existed, verified gone by grep before this rewrite.
//
// hallpass-backend has no endpoint that returns a persisted "school setup
// completed" flag (no GET /schools/:id, unlike Firestore's
// schoolConfig.setupCompleted doc the legacy checkSchoolSetupStatus read
// directly). checkSchoolSetupStatus below is reimplemented as a best-effort
// REST heuristic instead: listScheduleTypes(schoolId) is empty only before
// SchoolSetupWizard's first stage has ever run for this school, since
// schedule types are the first artifact that wizard creates. This has no
// other consumer, so it cannot regress anything, but it is a heuristic, not
// a real replacement for the removed setupCompleted flag.
//
// schoolConfig ({schoolType, totalPeriods}) is likewise not persisted
// anywhere as such — it's reconstructed from the real ScheduleType/Period
// data every time isSchoolConfigured resolves true (not just for the admin
// who ran the wizard this session): the same listScheduleTypes result is
// grouped with ScheduleConfigPanel's groupScheduleTypes, the base-named
// DEFAULT_VARIANT_NAME ("Regular") variant is preferred as "the" variant
// (falling back to the first group alphabetically if that name doesn't
// exist — e.g. it was renamed or deleted), split-ness is decided with the
// same isSplit check ScheduleVariantModal.js's effectiveSchoolConfig uses,
// and totalPeriods is that variant's relevant ScheduleType's period count.
// Note: `developerModeOrg` (the full org object, as opposed to
// `developerModeOrgId`) is part of AdminPortalWrapper.js's prop contract
// but was already unused in the legacy AdminPanel.js body — not
// destructured here to avoid an unused-var lint failure; passing it is
// still harmless for callers that do.
interface AdminPanelProps {
  developerModeOrgId?: number | null;
  organizationContext?: { currentOrgId?: number | null } | null;
}

const AdminPanel = ({
  developerModeOrgId = null,
  organizationContext = null,
}: AdminPanelProps): React.ReactElement => {
  const { currentUser, logout } = useAuth();

  // Use developer mode org if provided, otherwise use passed organization context
  const currentOrgId = developerModeOrgId || organizationContext?.currentOrgId;
  const {
    getCurrentPeriod,
    getScheduleType,
    getABDay,
    isAutoMode,
    isDeveloperMode,
    updateTrigger,
    scheduleVariantsMap = {},
  } = useDevOverride();

  const [currentPeriod, setCurrentPeriod] = useState<CurrentPeriod | null>(
    null
  );
  const [scheduleType, setScheduleType] = useState<string | null>(null);
  const [abDay, setABDay] = useState<string | null>('A');

  // Initial loading state
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Whether this school has completed initial setup. null = still checking
  // (show a loading message), true = render the dashboard, false = render
  // SchoolSetupWizard. See the header comment above.
  const [isSchoolConfigured, setIsSchoolConfigured] = useState<boolean | null>(
    null
  );
  const [schoolConfig, setSchoolConfig] = useState<SchoolConfig | null>(null);
  // The school's persisted timezone, fetched via GET /schools/:id (now
  // readable by an ADMIN for their own school, not just SUPER_ADMIN). Used
  // only to seed SchoolSetupWizard's timezone picker for the case where a
  // SUPER_ADMIN already created this school before the admin ever runs the
  // wizard — otherwise the picker would default to a hardcoded timezone
  // that may not match what's actually on the school record. Non-fatal if
  // it fails to load; the wizard just falls back to its hardcoded default.
  const [schoolTimezone, setSchoolTimezone] = useState<string | null>(null);

  useEffect(() => {
    // Update current period info
    const updateCurrentInfo = () => {
      try {
        // Ensure functions exist before calling
        if (!getScheduleType || !getCurrentPeriod || !getABDay) {
          console.warn('[AdminPanel] DevOverride functions not yet available');
          return;
        }

        const schedule = getScheduleType();
        const period = getCurrentPeriod();
        const currentABDay = getABDay();

        setScheduleType(schedule);
        setCurrentPeriod(period);
        setABDay(currentABDay);
      } catch (error) {
        console.error(
          '[AdminPanel] Error updating current period info:',
          error
        );
        // Set defaults to prevent crashes
        setScheduleType('regular');
        setCurrentPeriod(null);
        setABDay('A');
      }
    };

    // Initial update only - backend period detection handles the rest
    updateCurrentInfo();
    // No polling interval - DevOverrideContext listens to backend period state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateTrigger, currentOrgId]); // Only depend on data, not functions

  // Check school setup status when organization changes (REST heuristic —
  // see the header comment above) and, if configured, derive schoolConfig
  // from that same schedule-types response.
  useEffect(() => {
    if (!currentOrgId) return;

    let cancelled = false;
    setIsSchoolConfigured(null);

    (async () => {
      let scheduleTypes;
      try {
        const result = await listScheduleTypes(currentOrgId);
        if (cancelled) return;
        scheduleTypes = result.data;
        setIsSchoolConfigured(scheduleTypes.length > 0);
      } catch (error) {
        if (!cancelled) {
          logError(error, 'client_api', 'AdminPanel:checkSchoolSetupStatus');
          // On error, fall back to showing the setup wizard, same as the
          // legacy Firestore-based check did.
          setIsSchoolConfigured(false);
        }
        return;
      }

      if (scheduleTypes.length === 0) return;

      // Reconstruct {schoolType, totalPeriods} from the real data — see the
      // header comment above for the variant-selection/isSplit reasoning.
      // Failure here is non-fatal (schoolConfig just stays null, same as
      // the pre-existing gap), so it's isolated from the setup-status catch
      // above and never flips isSchoolConfigured back to false.
      try {
        const groups = groupScheduleTypes(scheduleTypes);
        const variant =
          groups.find((g) => g.variantName === DEFAULT_VARIANT_NAME) ||
          groups[0];
        const { single, aDay, bDay } = variant.scheduleTypeIds;
        const isSplit = Boolean(aDay || bDay);
        const relevantScheduleTypeId = isSplit ? aDay || bDay : single;
        if (!relevantScheduleTypeId) return;

        const periodsRes = await listPeriods(
          currentOrgId,
          relevantScheduleTypeId
        );
        if (!cancelled) {
          setSchoolConfig({
            schoolType: isSplit ? 'Block' : 'Regular',
            totalPeriods: periodsRes.data.length,
          });
        }
      } catch (error) {
        if (!cancelled) {
          logError(error, 'client_api', 'AdminPanel:deriveSchoolConfig');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentOrgId]);

  useEffect(() => {
    if (!currentOrgId) return;

    let cancelled = false;
    (async () => {
      try {
        const school = await getSchool(currentOrgId);
        if (!cancelled) setSchoolTimezone(school.timezone);
      } catch (error) {
        if (!cancelled) {
          logError(error, 'client_api', 'AdminPanel:getSchool');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentOrgId]);

  // Set initial loading to false once we have basic data
  useEffect(() => {
    if (currentUser && currentOrgId) {
      // Give a brief moment for UI to initialize
      const timer = setTimeout(() => {
        setIsInitialLoading(false);
      }, 750); // Slightly longer for admin panel

      return () => clearTimeout(timer);
    }
  }, [currentUser, currentOrgId]);

  const handleSetupComplete = (config: SchoolConfig): void => {
    setSchoolConfig(config);
    setIsSchoolConfigured(true);
  };

  // Show initial loading screen
  if (isInitialLoading) {
    return <LoadingScreen message="Loading admin panel..." />;
  }

  // Show school setup wizard if not configured
  if (isSchoolConfigured === null) {
    return (
      <div className={styles['dashboard']}>
        <div className={styles['loading-message']}>
          <h2>Loading school configuration...</h2>
        </div>
      </div>
    );
  }

  if (!isSchoolConfigured && currentOrgId) {
    return (
      <div className={styles['dashboard']}>
        <SchoolSetupWizard
          onSetupComplete={handleSetupComplete}
          currentOrgId={currentOrgId}
          existingTimezone={schoolTimezone}
        />
      </div>
    );
  }

  // The dashboard requires a resolved org and signed-in user (both are
  // always present once the branches above have fallen through in normal
  // flow); this guard narrows `currentOrgId` to a concrete number and
  // `currentUser` to non-null so the panels below (several of which declare
  // `schoolId` required and/or number-only) type-check without casts.
  if (currentOrgId == null || currentUser == null) {
    return (
      <div className={styles['dashboard']}>
        <div className={styles['loading-message']}>
          <h2>Loading school configuration...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className={styles['dashboard']}>
      <header>
        <div>
          <h1>
            Admin Panel
            {isDeveloperMode && (
              <span className={styles['dev-mode-badge']}>🔧 DEV MODE</span>
            )}
          </h1>
          {currentPeriod ? (
            <div className={styles['period-info']}>
              <span className={styles['current-period']}>
                {currentPeriod.displayName}
              </span>
              <span className={styles['ab-day']}>
                ({currentPeriod.abDay} Day)
              </span>
              <span className={styles['schedule-type']}>
                • {scheduleVariantsMap[scheduleType ?? ''] || scheduleType}
              </span>
              {isAutoMode && (
                <span className={styles['time-remaining']}>
                  • {currentPeriod.timeRemaining} min left
                </span>
              )}
            </div>
          ) : (
            <div className={styles['period-info']}>
              <span className={styles['no-period']}>No active period</span>
              {abDay && (
                <span className={styles['ab-day']}>({abDay} Day)</span>
              )}
              {scheduleType && (
                <span className={styles['schedule-type']}>
                  • {scheduleVariantsMap[scheduleType] || scheduleType}
                </span>
              )}
            </div>
          )}
        </div>
        <div className={styles['header-actions']}>
          <a
            href="https://docs.google.com/forms/d/e/1FAIpQLScdXhb7Qs0IOmSG3UoMLMqOP1CBpoSKFbdAe6iExkyrenIGqg/viewform?usp=header"
            target="_blank"
            rel="noopener noreferrer"
            className={styles['bug-report-link']}
          >
            🐛 Report Bug
          </a>
          <span>Welcome, {currentUser.name}</span>
          <button onClick={logout}>Logout</button>
        </div>
      </header>

      <SetupChecklist schoolId={currentOrgId} />

      <main>
        <div className={styles['admin-sections']}>
          <ActivePassesPanel schoolId={currentOrgId} />
          <UserManagementPanel schoolId={currentOrgId} />
          <DestinationsPanel schoolId={currentOrgId} />
          <SchoolPeriodsPanel schoolId={currentOrgId} />
          <ScheduleConfigPanel
            schoolId={currentOrgId}
            schoolConfig={schoolConfig ?? undefined}
          />
          <PassPolicyPanel schoolId={currentOrgId} />
          <StudentPassLimitsPanel schoolId={currentOrgId} />
          <ClassSectionsPanel schoolId={currentOrgId} />

          <CollapsibleSection
            title="Pass History Analytics"
            icon="📊"
            id="pass-history-analytics"
            defaultExpanded={false}
          >
            <div>
              <p className="text-muted" style={{ margin: '10px 0' }}>
                View detailed pass history and usage analytics for all students.
              </p>
              <PassHistoryView orgId={currentOrgId} />
            </div>
          </CollapsibleSection>
        </div>
      </main>
    </div>
  );
};

export default AdminPanel;
